import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collages, books, diaryEntries } from '../../data';
import useDocumentMeta from '../../hooks/useDocumentMeta';
import useReducedMotion from '../../hooks/useReducedMotion';
import * as d3 from 'd3';
import './Homepage.css';

const INTRO_SEEN_KEY = 'static-noise:intro-seen';

const TITLE = 'static.noise';
const INTRO_ONE =
    "Hopping around here and there, I collect fragments of other people's stories.";
const INTRO_TWO =
    'books in second-hand shops, photos in flea markets, diaries in old basements. I then try to write diary entries about them, and stitch them together to make collages.';

const introAlreadySeen = () => {
    try {
        return window.sessionStorage.getItem(INTRO_SEEN_KEY) === '1';
    } catch {
        return false;
    }
};

const markIntroSeen = () => {
    try {
        window.sessionStorage.setItem(INTRO_SEEN_KEY, '1');
    } catch {
        /* private browsing — the animation just replays, which is fine */
    }
};

/** Scrambles into `targetText`. Jumps straight to the end when `skip` is set. */
const useScrambleText = (
    targetText: string,
    skip: boolean,
    interval = 40,
    stepCount = 25,
    delay = 300
) => {
    const [displayText, setDisplayText] = useState('');

    useEffect(() => {
        if (skip) {
            setDisplayText(targetText);
            return;
        }

        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const targetArray = targetText.split('');
        let step = 0;
        let frame: ReturnType<typeof setTimeout>;

        const scramble = () => {
            if (step > stepCount) {
                setDisplayText(targetText);
                return;
            }
            const scrambled = targetArray.map((char, index) =>
                Math.random() > step / stepCount
                    ? chars[Math.floor(Math.random() * chars.length)]
                    : targetArray[index]
            );
            setDisplayText(scrambled.join(''));
            step++;
            frame = setTimeout(scramble, interval);
        };

        const delayTimeout = setTimeout(scramble, delay);

        return () => {
            clearTimeout(delayTimeout);
            clearTimeout(frame);
        };
    }, [targetText, skip, interval, stepCount, delay]);

    return displayText;
};

/** Types `text` out. Jumps straight to the end when `skip` is set. */
const useTypewriter = (text: string, skip: boolean, speed = 15, delay = 0) => {
    const [displayText, setDisplayText] = useState('');
    const [showCursor, setShowCursor] = useState(true);
    const [isComplete, setIsComplete] = useState(false);

    useEffect(() => {
        if (skip) {
            setDisplayText(text);
            setIsComplete(true);
            return;
        }

        setIsComplete(false);
        let timeoutId: ReturnType<typeof setTimeout>;
        let currentIndex = 0;

        const type = () => {
            if (currentIndex <= text.length) {
                setDisplayText(text.slice(0, currentIndex));
                currentIndex++;
                timeoutId = setTimeout(type, speed);
            } else {
                setIsComplete(true);
            }
        };

        const delayTimeout = setTimeout(type, delay);
        const cursorInterval = setInterval(() => setShowCursor(prev => !prev), 530);

        return () => {
            clearTimeout(timeoutId);
            clearTimeout(delayTimeout);
            clearInterval(cursorInterval);
        };
    }, [text, skip, speed, delay]);

    return { text: displayText, cursor: showCursor, isComplete };
};

interface HomepageProps {
    setIsNavbarVisible: (visible: boolean) => void;
    isNavbarVisible: boolean;
}

type NodeType = 'collage' | 'book' | 'diary';

interface NodeDatum extends d3.SimulationNodeDatum {
    id: string;
    type: NodeType;
    label: string;
}

interface LinkDatum {
    source: NodeDatum;
    target: NodeDatum;
}

/* Shape as well as colour. The three types used to be identical 8px
   squares separated only by red / green / blue — the exact pairing that
   red-green colour blindness collapses. The fills come from CSS custom
   properties so they follow the theme without re-running the layout. */
const SHAPES: Record<NodeType, d3.SymbolType> = {
    collage: d3.symbolSquare,
    book: d3.symbolCircle,
    diary: d3.symbolDiamond
};

const NODE_AREA = 150;
/** Invisible hit area. The visible marks are ~12px; WCAG asks for 24. */
const HIT_RADIUS = 14;

const Homepage: React.FC<HomepageProps> = () => {
    const networkRef = useRef<SVGSVGElement | null>(null);
    const navigate = useNavigate();
    const reduceMotion = useReducedMotion();

    // Handles for the zoom buttons. The wheel no longer zooms — it belongs
    // to the page — so the graph needs somewhere visible to offer it.
    const zoomRef = useRef<{
        svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
        zoom: d3.ZoomBehavior<SVGSVGElement, unknown>;
    } | null>(null);

    useDocumentMeta({
        description:
            "Hopping around here and there, I collect fragments of other people's stories — books from second-hand shops, photos from flea markets, diaries from old basements — and stitch them into collages."
    });

    // Skip the whole choreography for anyone who has already seen it this
    // session, or who has asked for reduced motion.
    const [skipIntro, setSkipIntro] = useState(() => introAlreadySeen() || reduceMotion);

    // Every type is visible from the start. These used to switch on only
    // as their keyword was typed, and "collages" is the last word of the
    // second sentence — so the collage nodes did not appear until roughly
    // twelve seconds in.
    const [visibleTypes, setVisibleTypes] = useState<Record<NodeType, boolean>>({
        collage: true,
        book: true,
        diary: true
    });

    const scrambledTitle = useScrambleText(TITLE, skipIntro);
    const introText = useTypewriter(INTRO_ONE, skipIntro, 15, 1200);
    const secondText = useTypewriter(
        INTRO_TWO,
        skipIntro,
        15,
        introText.isComplete ? 0 : Number.MAX_SAFE_INTEGER
    );

    const introRunning = !skipIntro && !secondText.isComplete;

    const finishIntro = useCallback(() => {
        setSkipIntro(true);
        markIntroSeen();
    }, []);

    // Click, tap or any key finishes the sequence immediately.
    useEffect(() => {
        if (!introRunning) {
            markIntroSeen();
            return;
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Tab') return; // let keyboard users move focus
            finishIntro();
        };
        window.addEventListener('pointerdown', finishIntro);
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('pointerdown', finishIntro);
            window.removeEventListener('keydown', onKey);
        };
    }, [introRunning, finishIntro]);

    const toggleNodeType = (type: NodeType) => {
        setVisibleTypes(prev => ({ ...prev, [type]: !prev[type] }));
    };

    const renderClickableItem = (type: NodeType, text: string) => (
        // Was a <span onClick>: invisible to the keyboard and announced as
        // plain text. aria-pressed makes the on/off state audible.
        <button
            type="button"
            className={`filter-item filter-${type} ${!visibleTypes[type] ? 'inactive' : ''}`}
            onClick={() => toggleNodeType(type)}
            aria-pressed={visibleTypes[type]}
            key={`${type}-${text}`}
        >
            <span className="color-square" aria-hidden="true" />
            <span className="filter-label">{text}</span>
        </button>
    );

    const processText = (text: string) =>
        text.split(/(books|diary entries|collages)/).map((part, index) => {
            switch (part) {
                case 'books':
                    return renderClickableItem('book', 'books');
                case 'diary entries':
                    return renderClickableItem('diary', 'diary entries');
                case 'collages':
                    return renderClickableItem('collage', 'collages');
                default:
                    return <React.Fragment key={index}>{part}</React.Fragment>;
            }
        });

    const { nodes, links } = useMemo(() => {
        const nextNodes: NodeDatum[] = [];

        if (visibleTypes.book) {
            books.forEach(book =>
                nextNodes.push({
                    id: book.id,
                    type: 'book',
                    label: `${book.title} (${book.publishYear})`
                })
            );
        }
        if (visibleTypes.collage) {
            collages.forEach(collage =>
                nextNodes.push({ id: collage.id, type: 'collage', label: collage.title })
            );
        }
        if (visibleTypes.diary) {
            diaryEntries.forEach(diary =>
                nextNodes.push({ id: diary.id, type: 'diary', label: diary.date })
            );
        }

        const byKey = new Map(nextNodes.map(n => [`${n.type}:${n.id}`, n]));
        const nextLinks: LinkDatum[] = [];

        if (visibleTypes.collage && visibleTypes.book) {
            collages.forEach(collage => {
                const source = byKey.get(`collage:${collage.id}`);
                if (!source) return;
                collage.bookIds.forEach(bookId => {
                    const target = byKey.get(`book:${bookId}`);
                    if (target) nextLinks.push({ source, target });
                });
            });
        }

        if (visibleTypes.book && visibleTypes.diary) {
            books.forEach(book => {
                const source = byKey.get(`book:${book.id}`);
                if (!source) return;
                book.diaryIds?.forEach(diaryId => {
                    const target = byKey.get(`diary:${diaryId}`);
                    if (target) nextLinks.push({ source, target });
                });
            });
        }

        return { nodes: nextNodes, links: nextLinks };
    }, [visibleTypes]);

    useEffect(() => {
        const svgEl = networkRef.current;
        if (!svgEl) return;

        const width = svgEl.clientWidth;
        const height = svgEl.clientHeight;
        if (!width || !height) return;

        const centerX = width / 2;
        const centerY = height / 2;

        nodes.forEach(n => {
            n.x = centerX;
            n.y = centerY;
        });

        d3.select(svgEl).selectAll('*').remove();

        // The tooltip used to be appended to <body> on every re-run and
        // never removed, so each filter toggle leaked another div.
        const tooltip = d3
            .select('body')
            .append('div')
            .attr('class', 'network-tooltip')
            .style('opacity', 0);

        const svg = d3.select(svgEl).attr('width', width).attr('height', height);
        const container = svg.append('g');

        const zoom = d3
            .zoom<SVGSVGElement, unknown>()
            .scaleExtent([0.1, 4])
            .on('zoom', event => container.attr('transform', event.transform))
            .filter(event => {
                if (event.type === 'mousedown') return event.button === 0;
                if (event.type === 'touchstart') return true;

                // A plain wheel belongs to the page. This graph fills most
                // of the viewport, so claiming the wheel meant the homepage
                // could not be scrolled at all — and the old filter called
                // preventDefault() before deciding, so on any touch-capable
                // laptop the wheel neither scrolled nor zoomed. Zooming is
                // on the same modifier the browser itself uses.
                if (event.type === 'wheel') return event.ctrlKey || event.metaKey;

                return true;
            });

        svg.call(zoom).call(zoom.transform, d3.zoomIdentity);
        svg.on('dblclick.zoom', null);
        zoomRef.current = { svg, zoom };

        const link = container
            .append('g')
            .selectAll('line')
            .data(links)
            .join('line')
            .attr('class', 'network-link');

        const symbol = d3.symbol<NodeDatum>()
            .type(d => SHAPES[d.type])
            .size(NODE_AREA);

        const node = container
            .append('g')
            .selectAll<SVGGElement, NodeDatum>('g')
            .data(nodes)
            .join('g')
            .attr('class', d => `network-node node-${d.type}`)
            .attr('tabindex', 0)
            .attr('role', 'link')
            .attr('aria-label', d => d.label)
            .style('cursor', 'pointer');

        // Transparent disc so the target is ~28px across even though the
        // visible mark is small.
        node.append('circle')
            .attr('class', 'node-hit')
            .attr('r', HIT_RADIUS)
            .attr('fill', 'transparent');

        node.append('path')
            .attr('class', 'node-mark')
            .attr('d', d => symbol(d));

        const go = (d: NodeDatum) => {
            const route = d.type === 'collage' ? 'collages' : d.type === 'book' ? 'books' : 'diary';
            navigate(`/${route}/${d.id}`);
        };

        node
            .on('click', (event, d) => {
                if (event.defaultPrevented) return;
                tooltip.style('opacity', 0);
                go(d);
            })
            .on('keydown', (event: KeyboardEvent, d) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    go(d);
                }
            })
            .on('mouseover focus', function (event, d) {
                tooltip
                    .attr('class', `network-tooltip ${d.type}-tooltip`)
                    .html(d.label)
                    .transition()
                    .duration(150)
                    .style('opacity', 0.95);

                const point = (event as MouseEvent).pageX
                    ? { x: (event as MouseEvent).pageX + 12, y: (event as MouseEvent).pageY - 8 }
                    : (() => {
                          const box = (this as SVGGElement).getBoundingClientRect();
                          return { x: box.right + window.scrollX, y: box.top + window.scrollY };
                      })();

                tooltip.style('left', `${point.x}px`).style('top', `${point.y}px`);
            })
            .on('mouseout blur', () => {
                tooltip.transition().duration(300).style('opacity', 0);
            })
            .call(
                d3
                    .drag<SVGGElement, NodeDatum>()
                    .on('start', (event, d) => {
                        if (!event.active) simulation.alphaTarget(0.3).restart();
                        d.fx = d.x;
                        d.fy = d.y;
                    })
                    .on('drag', (event, d) => {
                        d.fx = event.x;
                        d.fy = event.y;
                    })
                    .on('end', (event, d) => {
                        if (!event.active) simulation.alphaTarget(0);
                        d.fx = null;
                        d.fy = null;
                    })
            );

        // The separate label layer is gone. It was rendered for every node
        // but spent almost all of its life at opacity 0, and the tooltip —
        // which now fires on keyboard focus as well as hover — already
        // covers the same need without 100+ extra text elements in the
        // tick loop.
        const simulation = d3
            .forceSimulation<NodeDatum>(nodes)
            .force('link', d3.forceLink<NodeDatum, LinkDatum>(links).distance(100).strength(1))
            .force('charge', d3.forceManyBody().strength(-200).distanceMin(10).distanceMax(300))
            .force('center', d3.forceCenter(centerX, centerY).strength(0.1))
            .force('collision', d3.forceCollide().radius(HIT_RADIUS + 4).strength(1))
            .force('x', d3.forceX(centerX).strength(0.05))
            .force('y', d3.forceY(centerY).strength(0.05))
            .on('tick', () => applyPositions());

        function applyPositions() {
            link
                .attr('x1', d => d.source.x ?? 0)
                .attr('y1', d => d.source.y ?? 0)
                .attr('x2', d => d.target.x ?? 0)
                .attr('y2', d => d.target.y ?? 0);

            node.attr('transform', d => `translate(${d.x ?? 0},${d.y ?? 0})`);
        }

        if (reduceMotion) {
            // Settle the layout without animating it, then paint once.
            simulation.stop();
            simulation.tick(300);
            applyPositions();
        } else {
            simulation.alpha(1).restart();
        }

        return () => {
            simulation.stop();
            tooltip.remove();
            d3.select(svgEl).on('.zoom', null);
            zoomRef.current = null;
        };
    }, [nodes, links, navigate, reduceMotion]);

    const zoomBy = useCallback(
        (factor: number) => {
            const handle = zoomRef.current;
            if (!handle) return;
            handle.zoom.scaleBy(handle.svg.transition().duration(220), factor);
        },
        []
    );

    const resetZoom = useCallback(() => {
        const handle = zoomRef.current;
        if (!handle) return;
        handle.zoom.transform(handle.svg.transition().duration(300), d3.zoomIdentity);
    }, []);

    return (
        <div className="homepage visible">
            <div className="homepage-content">
                <div className="text-section">
                    <div className="network-intro">
                        <h1 className="scramble-text">{scrambledTitle}</h1>
                        <p className="typewriter-text">
                            {introText.text}
                            {introText.isComplete && (
                                <>{' '}{processText(secondText.text)}</>
                            )}
                            <span
                                className={`cursor ${
                                    introRunning &&
                                    (introText.isComplete ? secondText.cursor : introText.cursor)
                                        ? 'visible'
                                        : ''
                                }`}
                                aria-hidden="true"
                            >
                                |
                            </span>
                        </p>

                        {introRunning && (
                            <button
                                type="button"
                                className="intro-skip"
                                onClick={finishIntro}
                            >
                                skip intro
                            </button>
                        )}

                        {!introRunning && (
                            <p className="network-hint">
                                Toggle a category above, or pick any node to open it.
                                Drag the background to move the map around.
                            </p>
                        )}
                    </div>
                </div>
                <div className="network-section">
                    <svg
                        ref={networkRef}
                        className="network-graph"
                        role="group"
                        aria-label="Network of collages, books and diary entries"
                    />

                    {/* Buttons rather than the wheel: the wheel is how the
                        page scrolls, and this graph covers most of it. */}
                    <div className="network-zoom">
                        <button
                            type="button"
                            onClick={() => zoomBy(1.4)}
                            aria-label="Zoom in"
                        >
                            +
                        </button>
                        <button
                            type="button"
                            onClick={() => zoomBy(1 / 1.4)}
                            aria-label="Zoom out"
                        >
                            −
                        </button>
                        <button
                            type="button"
                            onClick={resetZoom}
                            aria-label="Reset the view"
                        >
                            ⤾
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Homepage;
